import { IconButton, Collapse, Card, CardHeader, Container, Typography, Stack, Avatar, Menu, MenuItem, TextField, Button, Badge, Box, Divider } from '@mui/material';
import { CompleteNoteModal } from './modals/CompleteNoteModal';
import { DeleteNoteModal } from './modals/DeleteNoteModal';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { User } from '@/_models/User';
import { getInitials } from '@/utils/getInitials';
import PublicAPI from '@/api/TreedisPublicDAO';
import socket from '@/components/util/socket';
import { formData } from '@/utils/formData';
import { groupThreadsByUpdateDate } from './data';
import { NoNotesFound } from './NoNotesFound';
import dayjs from 'dayjs';
import { NoteEditEventType } from '../Threads';
import Icon from '@/units/Icon';
import DeviceManager from '@/components/util/deviceManager';

export const ThreadsItems = ({
  rtl,
  threads,
  setThreads,
  onThreadItemSelect,
  collaborators,
  userId,
  showCaseTemp,
  showcaseTag,
  tourSlug,
  isTourAdmin,
  toggleThreadVisibility,
  hideElements,
  newThreadMessages,
  tagNode,
  inputNodeComponent,
  controls,
  removeThreadFromSpace,
  editMode,
  setEditMode,
  handleAddThread,
  moveToTag,
  setFilteredCount,
  onCompleteNote,
  isGuest,
}: {
  rtl: boolean;
  threads: any;
  setThreads: (threads: any) => void;
  onThreadItemSelect: (t: any) => void;
  collaborators: User[];
  userId: number;
  showCaseTemp: any;
  showcaseTag: any;
  tourSlug: string;
  removeThreadFromSpace: (thread: any) => Promise<void>;
  isTourAdmin: boolean;
  toggleThreadVisibility: (thread: any, isVisible: boolean) => void;
  hideElements: (data: any) => void;
  newThreadMessages: { [key: number]: number };
  tagNode: any;
  inputNodeComponent: any;
  controls: any;
  editMode: boolean;
  setEditMode: Dispatch<SetStateAction<boolean>>;
  handleAddThread: () => void;
  moveToTag: (tagSid: any) => void;
  setFilteredCount: (count: number) => void;
  onCompleteNote: (noteId: number, value?: any) => void;
  isGuest: boolean;
}) => {
  const [filteredThreads, setfilteredThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [open, setOpen] = useState<string | null>('Today');
  const [anchorEls, setAnchorEls] = useState<{ [key: string]: HTMLElement | null }>({});
  const [filterState, setFilterState] = useState('active');
  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null);
  const [openedMenuId, setOpenedMenuId] = useState(null);
  const [hoveredNoteId, setHoveredNoteId] = useState(null);
  const [isDeleteNoteModalOpen, setIsDeleteNoteModalOpen] = useState(false);
  const [isCompletionModalOpen, setIsCompletionModalOpen] = useState(false);
  const recentNote = useRef(null);
  const threadPosition = useRef(null);
  const isFilterOpen = Boolean(filterAnchor);
  const selectedThreadRef = useRef(null);

  const foundUser = collaborators?.find((c) => c.id === userId);

  useEffect(() => {
    let filtered;
    let threadsToHide;
    switch (filterState) {
      case 'active':
        filtered = threads.filter((t) => !t.isCompleted);
        threadsToHide = threads.filter((t) => t.isCompleted).map((t) => t.id);
        setfilteredThreads(filtered);
        setFilteredCount(filtered.length);
        if (threadsToHide.length) {
          hideElements({ hideTags: threadsToHide, polygonsToHide: [], vsImagesToHide: [], vsVideosToHide: [], vsItemsToHide: [] });
        }
        filtered.forEach((thread) => {
          toggleThreadVisibility(thread, true);
        });
        break;
      case 'completed':
        filtered = threads.filter((t) => t.isCompleted);
        threadsToHide = threads.filter((t) => !t.isCompleted).map((t) => t.id);
        setfilteredThreads(filtered);
        setFilteredCount(filtered.length);
        hideElements({ hideTags: threadsToHide, polygonsToHide: [], vsImagesToHide: [], vsVideosToHide: [], vsItemsToHide: [] });
        filtered.forEach((thread) => {
          toggleThreadVisibility(thread, true);
        });
        break;
      case 'all':
        setfilteredThreads(threads);
        setFilteredCount(threads.length);
        threads.forEach((thread) => {
          toggleThreadVisibility(thread, true);
        });
        break;
      default:
        setfilteredThreads(threads);
    }
  }, [threads, filterState, hideElements, toggleThreadVisibility]);

  const groupedThreads = useCallback(() => {
    return groupThreadsByUpdateDate(filteredThreads);
  }, [filteredThreads]);

  const openMenu = (e, threadId) => {
    e.stopPropagation();
    setAnchorEls((prev) => ({ ...prev, [threadId]: e.currentTarget }));
    setOpenedMenuId(threadId);
  };

  const openFilter = (e) => {
    setFilterAnchor(e.currentTarget);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setAnchorEls((prev) => ({ ...prev, [openedMenuId]: null }));
    setOpenedMenuId(null);
  };

  const handleCloseFilter = () => {
    setFilterAnchor(null);
  };

  const handleEditPosition = useCallback(
    async (thread) => {
      setEditMode(true);
      if (threadPosition.current === null) threadPosition.current = { x: thread.positionX, y: thread.positionY, z: thread.positionZ };

      const inShowcaseTemp = showCaseTemp.tag.allTags && showCaseTemp.tag.allTags.has(thread.sid);
      recentNote.current = inShowcaseTemp;

      // if recently added thead it will be stored in shocaseTemp, editing will be available there
      // otherwise, tag was set in showcase
      if (inShowcaseTemp) {
        showCaseTemp.tag.startTranslateControls({
          tag: thread,
          tagNode,
          inputNodeComponent,
          controls,
          cb: () => {
            const { x, y, z } = tagNode.current.obj3D.position;
            thread.positionX = x;
            thread.positionY = y;
            thread.positionZ = z;
            showCaseTemp.tag.edit(thread);
          },
        });
      } else {
        if (showcaseTag && controls.current) {
          const intervalId = setInterval(() => {
            const threeJsTag = controls.current?.inputs?.selection?.obj3D?.children[0];
            if (threeJsTag) {
              threeJsTag.children.forEach((obj: any) => {
                obj.visible = false;
              });
              clearInterval(intervalId);
            }
          }, 10);

          await showCaseTemp.tag.startTranslateControls({
            tag: thread,
            tagNode,
            inputNodeComponent,
            controls,
            cb: () => {
              const { x, y, z } = tagNode.current.obj3D.position;
              thread.positionX = x;
              thread.positionY = y;
              thread.positionZ = z;
              showcaseTag.editPosition(thread.sid, {
                anchorPosition: {
                  x: thread.positionX,
                  y: thread.positionY,
                  z: thread.positionZ,
                },
              });
            },
          });
        }
      }

      setSelectedThread(thread);
      setAnchorEls((prev) => ({ ...prev, [openedMenuId]: null }));
    },
    [controls, inputNodeComponent, openedMenuId, setEditMode, showCaseTemp.tag, tagNode, showcaseTag],
  );

  const handleCancleEditPosition = useCallback(
    async (thread) => {
      thread.positionX = threadPosition.current.x;
      thread.positionY = threadPosition.current.y;
      thread.positionZ = threadPosition.current.z;
      showCaseTemp.tag.stopPositionUpdates({ controls });
      if (thread.isRecentNote) {
        await showCaseTemp.tag.edit(thread);
      } else {
        await showcaseTag.editPosition(thread.sid, {
          anchorPosition: {
            x: thread.positionX,
            y: thread.positionY,
            z: thread.positionZ,
          },
        });
      }
      if (tagNode.current) {
        tagNode.current.stop();
        tagNode.current = undefined;
      }
      if (inputNodeComponent.current) {
        inputNodeComponent.current = undefined;
      }
      controls.current.inputs.selection = null;
      threadPosition.current = null;
      setEditMode(false);
      setSelectedThread(null);
    },
    [controls, inputNodeComponent, setEditMode, showCaseTemp.tag, tagNode, showcaseTag],
  );

  const handleSavePosition = async (thread) => {
    delete thread.message;
    delete thread.dummyObject;
    delete thread.description;
    delete thread.mediaUrls;

    const dataToSave = formData.create({
      ...thread,
    });

    const result = await PublicAPI.saveThread({
      data: dataToSave,
    });

    if (result.message === 'Success') {
      const { savedThread } = result;
      showCaseTemp.tag.stopPositionUpdates({ controls });
      if (tagNode.current) {
        tagNode.current.stop();
        tagNode.current = undefined;
      }
      if (inputNodeComponent.current) {
        inputNodeComponent.current = undefined;
      }
      controls.current.inputs.selection = null;
      threadPosition.current = null;
      setEditMode(false);
      setSelectedThread(null);

      socket.emit('thread_edit', { ...savedThread, room: tourSlug, eventType: NoteEditEventType.EditPosition, isRecentNote: recentNote.current });
      recentNote.current = null;
    }
  };

  const handleSearch = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    const result = searchTerm
      ? threads.filter(
          (t) =>
            t.reporter.toLowerCase().includes(searchTerm) ||
            t.title.toLowerCase().includes(searchTerm) ||
            t.messages.find((message) => message.description.toLowerCase().includes(searchTerm)),
        )
      : threads;
    setfilteredThreads(result);
  };

  const filterByStatus = (e) => {
    const { id } = e.currentTarget;
    setFilterAnchor(null);
    setFilterState(id);
  };

  return (
    <>
      <Stack direction="row" gap={2} width="100%">
        <TextField
          variant="outlined"
          onChange={handleSearch}
          InputProps={{
            startAdornment: <Icon.Search sx={{ fontSize: '20px', fontWeight: '300', marginRight: '7px', color: '#667085' }} />,
            sx: {
              '& input::placeholder': {
                color: '#667085',
                opacity: 1,
                fontSize: '16px',
              },
            },
          }}
          inputProps={{
            sx: {
              maxHeight: '44px',
              height: '100%',
            },
          }}
          sx={{
            borderRadius: '8px',
            border: '1px solid #D1D1D1',
            background: '#FFF',
            width: '100%',
            '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
            '& fieldset': { height: '100%', top: 0 },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D0D5DD',
              borderRadius: '8px',
            },
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              background: '#FFF',
            },
          }}
          placeholder="Search"
        />

        {!threads.length ? (
          <IconButton
            onClick={handleAddThread}
            disabled={isGuest}
            id="add-button"
            aria-controls={isFilterOpen ? 'filter-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={isFilterOpen ? 'true' : undefined}
            sx={{
              borderRadius: '8px',
              border: '1px solid #D1D1D1',
              background: '#FFF',
              width: '44px',
              height: '44px',
              '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
              fieldset: { height: '100%', top: 0 },
            }}
          >
            <i className="fa-regular fa-plus" style={{ fontSize: '16px', color: '#2055FF' }} />
          </IconButton>
        ) : (
          <>
            <IconButton
              onClick={openFilter}
              id="filter-button"
              aria-controls={isFilterOpen ? 'filter-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={isFilterOpen ? 'true' : undefined}
              sx={{
                borderRadius: '8px',
                border: '1px solid #D0D5DD !important',
                background: '#FFF',
                width: '44px',
                height: '44px',
                '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
                fieldset: { height: '100%', top: 0 },
              }}
            >
              <i className="fa-regular fa-filter" style={{ fontSize: '20px', color: '#667085' }} />
            </IconButton>
            <Menu
              id="filter-menu"
              anchorEl={filterAnchor}
              open={isFilterOpen}
              onClose={handleCloseFilter}
              sx={{ width: '270px', '.MuiPaper-root': { background: 'white', width: '270px', border: '1px solid #EAECF0', borderRadius: '8px' } }}
              MenuListProps={{
                'aria-labelledby': 'filter-button',
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem id="all" onClick={filterByStatus}>
                {`Show all notes`}
                {filterState === 'all' && <i className="fa fa-check" style={{ color: '#2055ff', marginLeft: 'auto' }} />}
              </MenuItem>
              <MenuItem id="active" onClick={filterByStatus}>
                {`Only Incomplete notes`}
                {filterState === 'active' && <i className="fa fa-check" style={{ color: '#2055ff', marginLeft: 'auto' }} />}
              </MenuItem>
              <MenuItem id="completed" onClick={filterByStatus}>
                {`Only Completed notes`}
                {filterState === 'completed' && <i className="fa fa-check" style={{ color: '#2055ff', marginLeft: 'auto' }} />}
              </MenuItem>
            </Menu>
          </>
        )}
      </Stack>

      {!filteredThreads.length && <NoNotesFound handleAddThread={handleAddThread} isCompleted={filterState === 'completed'} setFilterState={setFilterState} isGuest={isGuest} />}

      {Object.entries(groupedThreads()).map(([group, insideThreads]) => {
        return (
          <Card sx={{ border: '1px solid #D0D5DD', borderRadius: '16px', background: 'white', backdropFilter: 'none', padding: '16px' }} key={group}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ marginRight: '6px', fontWeight: '700', fontSize: '16px', color: '#101828' }}>
                    {group}
                  </Box>
                  <Box
                    component="div"
                    sx={{
                      backgroundColor: '#F2F4F7',
                      borderRadius: '50%',
                      display: 'inline-flex',
                      width: '24px',
                      height: '24px',
                      textAlign: 'center',
                      padding: '4px',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: '14px',
                        color: '#2055FF',
                      }}
                    >
                      {insideThreads.length || 0}
                    </Box>
                  </Box>
                </Box>
              }
              sx={{ textAlign: rtl ? 'right' : 'left', direction: rtl ? 'rtl' : '', padding: 0 }}
              action={
                <IconButton onClick={() => setOpen(open === group ? null : group)} aria-label="expand" size="small" sx={{ marginRight: 2, marginTop: '5px' }}>
                  {open === group ? <i className="fa-solid fa-chevron-up" /> : <i className="fa-solid fa-chevron-down" />}
                </IconButton>
              }
            />
            <Collapse in={open === group} timeout="auto" unmountOnExit>
              <Stack gap={1}>
                {insideThreads.map((t: any) => {
                  const foundUser = collaborators?.find((c) => c.id === t.userId);
                  const date = dayjs(t.createdAt);
                  const formattedDate = date.format('MMMM D, hh:mm A');
                  const repliesCount = t?.messages?.length ? t?.messages?.length - 1 : 0;

                  return (
                    <Container
                      key={t.id}
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        gap: '10px',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        my: 2,
                        lineHeight: 2,
                        cursor: 'pointer',
                        opacity: t.isCompleted ? 0.5 : 1,
                        padding: '16px',
                        border: '1px solid #EAECF0',
                        borderRadius: '12px',
                        '&:hover': {
                          background: '#F2F4F7',
                        },
                      }}
                      onClick={() => onThreadItemSelect(t)}
                      onMouseEnter={() => setHoveredNoteId(t.id)}
                      onMouseLeave={() => setHoveredNoteId(null)}
                    >
                      <Badge
                        badgeContent={newThreadMessages[t.id] || 0}
                        color="error"
                        sx={{
                          alignSelf: 'flex-start',
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: userId === foundUser?.id ? '#2055ff' : 'white',
                            color: userId === foundUser?.id ? 'white' : 'black',
                            border: '1px solid #D1D1D1',
                          }}
                        >
                          {getInitials(foundUser?.name || 'Guest')}
                        </Avatar>
                      </Badge>
                      <Stack direction="column" width="100%">
                        <Typography
                          sx={{
                            maxWidth: '210px',
                            fontWeight: 600,
                            textAlign: rtl ? 'right' : 'left',
                            fontSize: '14px',
                          }}
                        >
                          {t?.title || '...'}
                        </Typography>
                        <Typography variant="body1" lineHeight={2} sx={{ textAlign: rtl ? 'right' : 'left', color: '#475467', fontWeight: '400', fontSize: '14px' }}>
                          {`Created: ${formattedDate}`}
                        </Typography>
                        <Typography variant="body1" lineHeight={2} sx={{ textAlign: rtl ? 'right' : 'left', color: '#475467', fontWeight: '400', fontSize: '14px' }}>
                          {`${repliesCount} ${repliesCount === 1 ? 'Reply' : 'Replies'}`}
                        </Typography>
                        {editMode && t.id === selectedThread?.id && (
                          <Stack direction="row" gap={2}>
                            <Button
                              variant="outlined"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancleEditPosition(t);
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSavePosition(t);
                              }}
                            >
                              Save
                            </Button>
                          </Stack>
                        )}
                      </Stack>
                      {DeviceManager.isMobile() ||
                        DeviceManager.isTablet() ||
                        (hoveredNoteId === t.id && (
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{
                              position: 'absolute',
                              right: '16px',
                              top: '16px',
                            }}
                          >
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                moveToTag(t.sid);
                              }}
                              id={`target-button-${selectedThread?.id}`}
                              sx={{
                                width: '36px',
                                height: '36px',
                              }}
                            >
                              <Box
                                component="i"
                                className="fa-regular fa-location-crosshairs"
                                sx={{
                                  color: '#667085',
                                  fontSize: '24px',
                                  fontWeight: 400,
                                }}
                              />
                            </IconButton>
                            {t?.userId === userId && (
                              <>
                                <IconButton
                                  onClick={(e) => openMenu(e, t.id)}
                                  id={`thread-button-${t.id}`}
                                  sx={{
                                    width: '36px',
                                    height: '36px',
                                  }}
                                >
                                  <Box
                                    component="i"
                                    className="fa-solid fa-ellipsis-vertical"
                                    sx={{
                                      color: '#667085',
                                      fontSize: '24px',
                                      fontWeight: 400,
                                    }}
                                  />
                                </IconButton>
                                <Menu
                                  key={`thread-menu-${t.id}`}
                                  id={`thread-menu-${t.id}`}
                                  anchorEl={anchorEls[t.id]}
                                  open={Boolean(anchorEls[t.id])}
                                  onClose={handleClose}
                                  sx={{ '.MuiPaper-root': { background: 'white', borderRadius: '8px' } }}
                                  MenuListProps={{
                                    'aria-labelledby': `thread-button-${t.id}`,
                                  }}
                                  anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                  }}
                                  transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                  }}
                                >
                                  <MenuItem
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditPosition(t);
                                    }}
                                    style={{ display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'center', width: '100%' }}
                                  >
                                    <i className="fa-regular fa-pen-to-square" style={{ fontSize: '13px' }} />
                                    <Typography style={{ fontSize: '14px' }}>Edit Position</Typography>
                                  </MenuItem>
                                  {t?.isCompleted ? (
                                    <MenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        selectedThreadRef.current = threads.find((t) => t.id === openedMenuId);
                                        setIsCompletionModalOpen(true);
                                        handleClose(e);
                                      }}
                                      style={{ display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'center', width: '100%' }}
                                    >
                                      <i className="fa-solid fa-arrow-rotate-left" style={{ fontSize: '13px' }} />

                                      <Typography style={{ fontSize: '14px' }}>Mark as Incomplete</Typography>
                                    </MenuItem>
                                  ) : (
                                    <MenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        selectedThreadRef.current = threads.find((t) => t.id === openedMenuId);
                                        setIsCompletionModalOpen(true);
                                        handleClose(e);
                                      }}
                                      style={{ display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'center', width: '100%' }}
                                    >
                                      <i className="fa fa-check" style={{ color: '#12B76A', fontSize: '13px' }} />

                                      <Typography style={{ fontSize: '14px' }}>Mark as Complete</Typography>
                                    </MenuItem>
                                  )}
                                  <Divider />
                                  <MenuItem
                                    id={t.id}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      selectedThreadRef.current = threads.find((t) => t.id === openedMenuId);
                                      setIsDeleteNoteModalOpen(true);
                                    }}
                                    style={{ display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'center', width: '100%', color: 'red' }}
                                  >
                                    <i className="fa-regular fa-trash-can" style={{ fontSize: '13px' }} />
                                    <Typography style={{ fontSize: '14px' }}>Delete</Typography>
                                  </MenuItem>
                                </Menu>
                              </>
                            )}
                          </Stack>
                        ))}
                    </Container>
                  );
                })}
              </Stack>
            </Collapse>
          </Card>
        );
      })}
      {isCompletionModalOpen && (
        <CompleteNoteModal
          isCompletionModalOpen={isCompletionModalOpen}
          handleClose={() => setIsCompletionModalOpen(false)}
          selectedNote={selectedThreadRef.current}
          foundUser={foundUser}
          completeNote={onCompleteNote}
        />
      )}
      {isDeleteNoteModalOpen && (
        <DeleteNoteModal
          isDeleteNoteModalOpen={isDeleteNoteModalOpen}
          setIsDeleteNoteModalOpen={setIsDeleteNoteModalOpen}
          setThreads={setThreads}
          selectedThread={selectedThreadRef.current}
          threads={threads}
          tourSlug={tourSlug}
          userId={foundUser.id}
          removeThreadFromSpace={removeThreadFromSpace}
        />
      )}
    </>
  );
};
